/* eslint-disable no-underscore-dangle */
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getCookies } from '../../../../hooks/useCookies';
import { REACT_APP_API_ADMIN_ROUTES } from '../../../../redux/apiConstants';
import envValues from '../../../../enviornment';

const MAPBOX_TOKEN =
  process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || envValues.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

/** Colour palette for the multi-route canvas */
export const CANVAS_COLORS = [
  '#e53935', // red   — default first route
  '#1565c0', // blue
  '#2e7d32', // green
  '#f57c00', // orange
  '#6a1b9a', // purple
  '#00838f', // teal
  '#d81b60', // pink
  '#4e342e', // brown
];

let routeSeq = 0;
const nextRouteId = () => { routeSeq += 1; return `canvas-${routeSeq}`; };

/**
 * Pick the next colour in the palette that's not currently used on the canvas.
 * @param {string[]} usedColors
 * @returns {string}
 */
const pickColor = (usedColors) =>
  CANVAS_COLORS.find((c) => !usedColors.includes(c)) || CANVAS_COLORS[routeSeq % CANVAS_COLORS.length];

/** Max waypoints per Mapbox Directions API call */
const DIRECTIONS_CHUNK = 25;

/**
 * Fetch a Directions route segment from Mapbox.
 * @param {[number,number][]} chunkWps - up to 25 [lng, lat] pairs
 * @param {string} profile - driving | walking | cycling
 * @returns {Promise<[number,number][]>} dense coordinate array
 */
const fetchDirectionsChunk = async (chunkWps, profile) => {
  const coords = chunkWps.map(([lng, lat]) => `${lng},${lat}`).join(';');
  const url =
    `https://api.mapbox.com/directions/v5/mapbox/${profile}/${coords}` +
    `?access_token=${MAPBOX_TOKEN}&geometries=geojson&overview=full&steps=false`;
  const res = await fetch(url);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Directions API responded with ${res.status}`);
  }
  const data = await res.json();
  if (!data.routes?.length) throw new Error('No route found between these points.');
  return data.routes[0].geometry.coordinates;
};

/**
 * Haversine distance between two [lng, lat] coordinates in km.
 * @param {[number, number]} coord1
 * @param {[number, number]} coord2
 * @returns {number}
 */
const haversineDistance = (coord1, coord2) => {
  const R = 6371;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(coord2[1] - coord1[1]);
  const dLng = toRad(coord2[0] - coord1[0]);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(coord1[1])) * Math.cos(toRad(coord2[1])) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const areCoordsEqual = (c1, c2, epsilon = 0.0001) =>
  Math.abs(c1[0] - c2[0]) < epsilon && Math.abs(c1[1] - c2[1]) < epsilon;

const getAuthHeaders = () => {
  const token = getCookies('Token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const useRouteDrawing = () => {
  /** @type {[[number,number][], React.Dispatch]} */
  const [waypoints, setWaypoints]           = useState([]);
  /**
   * Parallel array of optional stop names; always same length as `waypoints`.
   * @type {[string[], React.Dispatch]}
   */
  const [waypointLabels, setWaypointLabels] = useState([]);

  const [routeName, setRouteName]           = useState('');
  const [routeDescription, setRouteDescription] = useState('');
  const [selectedIndex, setSelectedIndex]   = useState(null);
  const [savedRoutes, setSavedRoutes]       = useState([]);
  const [editingRouteId, setEditingRouteId] = useState(null);
  const [isLoadingRoutes, setIsLoadingRoutes] = useState(true);
  const [isSaving, setIsSaving]             = useState(false);
  const [routeMapFocus, setRouteMapFocus]   = useState(null);

  /**
   * Index of the stop the user has chosen as the current draw-from point.
   * When set, every map click inserts a new stop right AFTER this index
   * and then advances the index by 1 so clicks chain continuously.
   * null = default behaviour (append at the very end).
   */
  const [drawFromIndex, setDrawFromIndex] = useState(null);

  // ─── Multi-route canvas ───────────────────────────────────────────────────
  /**
   * Colour assigned to the route currently being edited.
   * Each route on the canvas keeps its own colour for the session.
   */
  const [activeRouteColor, setActiveRouteColor] = useState(CANVAS_COLORS[0]);

  /**
   * Routes on the map canvas that are NOT the active editing route.
   * Each entry: { id, color, waypoints, waypointLabels, name, description,
   *              editingRouteId, generatedCoordinates }
   */
  const [canvasRoutes, setCanvasRoutes] = useState([]);

  // ─── Route generation (Mapbox Directions API) ─────────────────────────────
  /**
   * Dense coordinate array returned by the Directions API.
   * When set, geoJson and totalDistanceKm use this instead of raw waypoints.
   */
  const [generatedCoordinates, setGeneratedCoordinates] = useState(null);
  const [isGenerating, setIsGenerating]                 = useState(false);
  const [routeProfile, setRouteProfile]                 = useState('driving');

  // Track the waypoints snapshot that produced the last generation so we
  // can show a "stale" warning if the user edits stops afterwards.
  const generatedAtRef = useRef(null);

  // ─── Derived ─────────────────────────────────────────────────────────────

  /** True when the user has modified stops since the last generation */
  const isRouteStale = useMemo(() => {
    if (!generatedCoordinates || !generatedAtRef.current) return false;
    const prev = generatedAtRef.current;
    if (prev.length !== waypoints.length) return true;
    return prev.some((c, i) => c[0] !== waypoints[i][0] || c[1] !== waypoints[i][1]);
  }, [waypoints, generatedCoordinates]);

  /** Coordinates actually used for display & save — prefer generated path */
  const activeCoordinates = generatedCoordinates || waypoints;

  const totalDistanceKm = useMemo(() => {
    const coords = activeCoordinates;
    if (coords.length < 2) return 0;
    return coords.reduce((acc, wp, i) => {
      if (i === 0) return acc;
      return acc + haversineDistance(coords[i - 1], wp);
    }, 0);
  }, [activeCoordinates]);

  const geoJson = useMemo(
    () => ({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: activeCoordinates },
      properties: { name: routeName, description: routeDescription },
    }),
    [activeCoordinates, routeName, routeDescription],
  );

  // ─── Route generation ────────────────────────────────────────────────────

  /**
   * Calls the Mapbox Directions API in chunks of 25 waypoints.
   * Stitches the resulting dense coordinate arrays together.
   */
  const handleGenerateRoute = useCallback(async () => {
    if (waypoints.length < 2) {
      toast.error('Add at least 2 stops to generate a route.');
      return;
    }
    if (!MAPBOX_TOKEN) {
      toast.error('Mapbox token is not configured.');
      return;
    }
    setIsGenerating(true);
    try {
      // Split into overlapping chunks: [0..24], [24..48], ...
      const chunks = [];
      for (let i = 0; i < waypoints.length - 1; i += DIRECTIONS_CHUNK - 1) {
        chunks.push(waypoints.slice(i, i + DIRECTIONS_CHUNK));
      }

      let allCoords = [];
      for (let i = 0; i < chunks.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const chunkCoords = await fetchDirectionsChunk(chunks[i], routeProfile);
        if (i === 0) {
          allCoords = chunkCoords;
        } else {
          // Drop the first point — it's the junction already in the previous chunk
          allCoords = [...allCoords, ...chunkCoords.slice(1)];
        }
      }

      setGeneratedCoordinates(allCoords);
      generatedAtRef.current = [...waypoints];
      const distKm = allCoords.reduce((acc, wp, i) => {
        if (i === 0) return acc;
        return acc + haversineDistance(allCoords[i - 1], wp);
      }, 0);
      toast.success(
        `Route generated — ${allCoords.length.toLocaleString()} path points, ${distKm.toFixed(1)} km.`,
      );
    } catch (err) {
      console.error('[GenerateRoute]', err);
      toast.error(err.message || 'Failed to generate route. Check your stops and try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [waypoints, routeProfile]);

  const handleClearGeneratedRoute = useCallback(() => {
    setGeneratedCoordinates(null);
    generatedAtRef.current = null;
  }, []);

  // ─── Helper: resize labels to match waypoints length ─────────────────────
  const syncLabels = (prevLabels, prevLen, nextLen) => {
    if (nextLen > prevLen) {
      return [...prevLabels, ...Array(nextLen - prevLen).fill('')];
    }
    return prevLabels.slice(0, nextLen);
  };

  // ─── Map click — insert after drawFromIndex OR append at end ─────────────
  const handleMapClick = useCallback((e) => {
    const { lng, lat } = e.lngLat;
    const newCoord = [parseFloat(lng.toFixed(6)), parseFloat(lat.toFixed(6))];

    setDrawFromIndex((prevFrom) => {
      if (prevFrom !== null) {
        // Insert right after the current draw-from stop
        setWaypoints((prev) => {
          const next = [...prev];
          next.splice(prevFrom + 1, 0, newCoord);
          return next;
        });
        setWaypointLabels((prev) => {
          const next = [...prev];
          next.splice(prevFrom + 1, 0, '');
          return next;
        });
        // Auto-advance so the next click chains from the newly added stop
        return prevFrom + 1;
      }

      // Default — append at the very end
      setWaypoints((prev) => {
        if (prev.length > 0 && areCoordsEqual(prev[prev.length - 1], newCoord)) return prev;
        return [...prev, newCoord];
      });
      setWaypointLabels((prev) => [...prev, '']);
      return null; // keep drawFromIndex null
    });
  }, []);

  /**
   * Toggle draw-from mode for a stop.
   * Clicking the same stop again cancels the mode.
   * @param {number} index
   */
  const handleSetDrawFrom = useCallback((index) => {
    setDrawFromIndex((prev) => (prev === index ? null : index));
  }, []);

  /** Cancel draw-from mode (e.g. Escape key) */
  const handleCancelDrawFrom = useCallback(() => setDrawFromIndex(null), []);

  // ─── Multi-route helpers ──────────────────────────────────────────────────

  /** Snapshot of the current active editing state (used before switching) */
  const snapshotActive = useCallback((id, color, wps, wls, nm, desc, editId, genCoords) => ({
    id: id || nextRouteId(),
    color,
    waypoints:            wps,
    waypointLabels:       wls,
    name:                 nm,
    description:          desc,
    editingRouteId:       editId,
    generatedCoordinates: genCoords,
  }), []);

  /**
   * Push the current active route to the canvas (for display) and clear the
   * active editing state, ready to start a brand-new route.
   * @param {[number,number]?} firstCoord - optional first waypoint for the new route
   * @param {string} firstLabel
   */
  const handleNewRoute = useCallback((firstCoord = null, firstLabel = '') => {
    // Persist current route on canvas only if it has waypoints
    if (waypoints.length > 0) {
      const snapshot = snapshotActive(
        null, activeRouteColor, waypoints, waypointLabels,
        routeName, routeDescription, editingRouteId, generatedCoordinates,
      );
      setCanvasRoutes((prev) => {
        const existing = prev.findIndex((r) => r.color === activeRouteColor);
        if (existing >= 0) {
          const next = [...prev];
          next[existing] = snapshot;
          return next;
        }
        return [...prev, snapshot];
      });
    }

    // Pick next available colour for the new route (use current snapshot of canvas)
    setCanvasRoutes((prev) => {
      const used = prev.map((r) => r.color);
      if (waypoints.length > 0) used.push(activeRouteColor);
      setActiveRouteColor(pickColor(used));
      return prev; // no structural change — only used for access to latest prev
    });

    // Reset all active state
    setWaypoints(firstCoord ? [firstCoord] : []);
    setWaypointLabels(firstCoord ? [firstLabel] : []);
    setRouteName('');
    setRouteDescription('');
    setSelectedIndex(null);
    setEditingRouteId(null);
    setDrawFromIndex(firstCoord ? 0 : null);
    setGeneratedCoordinates(null);
    generatedAtRef.current = null;
  }, [
    waypoints, waypointLabels, routeName, routeDescription,
    editingRouteId, generatedCoordinates, activeRouteColor, snapshotActive,
  ]);

  /**
   * Branch a new separate route starting at an existing stop's coordinate.
   * @param {number} stopIndex
   */
  const handleBranchFromStop = useCallback((stopIndex) => {
    if (stopIndex < 0 || stopIndex >= waypoints.length) return;
    const coord = waypoints[stopIndex];
    const label = waypointLabels[stopIndex] || '';
    handleNewRoute(coord, label);
  }, [waypoints, waypointLabels, handleNewRoute]);

  /**
   * Switch active editing to a canvas route by id.
   * Current active route is snapshotted back to canvas.
   * @param {string} canvasId
   */
  const handleSwitchToRoute = useCallback((canvasId) => {
    const target = canvasRoutes.find((r) => r.id === canvasId);
    if (!target) return;

    // Build new canvas: remove target, optionally replace/add active snapshot
    const withoutTarget = canvasRoutes.filter((r) => r.id !== canvasId);
    let nextCanvas;
    if (waypoints.length > 0) {
      const activeSnap = snapshotActive(
        null, activeRouteColor, waypoints, waypointLabels,
        routeName, routeDescription, editingRouteId, generatedCoordinates,
      );
      const existingIdx = withoutTarget.findIndex((r) => r.color === activeRouteColor);
      if (existingIdx >= 0) {
        nextCanvas = [...withoutTarget];
        nextCanvas[existingIdx] = activeSnap;
      } else {
        nextCanvas = [...withoutTarget, activeSnap];
      }
    } else {
      nextCanvas = withoutTarget;
    }

    setCanvasRoutes(nextCanvas);

    // Load target into active state
    setActiveRouteColor(target.color);
    setWaypoints(target.waypoints);
    setWaypointLabels(target.waypointLabels);
    setRouteName(target.name);
    setRouteDescription(target.description);
    setEditingRouteId(target.editingRouteId || null);
    setGeneratedCoordinates(target.generatedCoordinates || null);
    generatedAtRef.current = null;
    setSelectedIndex(null);
    setDrawFromIndex(null);
  }, [
    canvasRoutes, activeRouteColor, waypoints, waypointLabels,
    routeName, routeDescription, editingRouteId, generatedCoordinates, snapshotActive,
  ]);

  /** Remove a canvas route from the map without switching to it. */
  const handleRemoveCanvasRoute = useCallback((canvasId) => {
    setCanvasRoutes((prev) => prev.filter((r) => r.id !== canvasId));
  }, []);

  // ─── Drag waypoint marker ─────────────────────────────────────────────────
  const handleWaypointDrag = useCallback((index, e) => {
    const { lng, lat } = e.lngLat;
    setWaypoints((prev) => {
      const next = [...prev];
      next[index] = [parseFloat(lng.toFixed(6)), parseFloat(lat.toFixed(6))];
      return next;
    });
  }, []);

  // ─── Delete a waypoint ────────────────────────────────────────────────────
  const handleDeleteWaypoint = useCallback((index) => {
    setWaypoints((prev) => prev.filter((_, i) => i !== index));
    setWaypointLabels((prev) => prev.filter((_, i) => i !== index));
    setSelectedIndex((prev) => {
      if (prev === null) return null;
      if (prev === index) return null;
      if (prev > index) return prev - 1;
      return prev;
    });
    setDrawFromIndex((prev) => {
      if (prev === null) return null;
      if (prev === index) return null;       // deleted the from-stop — cancel mode
      if (prev > index) return prev - 1;    // shift index down
      return prev;
    });
  }, []);

  // ─── Insert intermediate waypoint ────────────────────────────────────────
  const handleInsertWaypoint = useCallback((afterIndex, coord) => {
    const newCoord = [parseFloat(coord[0].toFixed(6)), parseFloat(coord[1].toFixed(6))];
    setWaypoints((prev) => {
      const next = [...prev];
      next.splice(afterIndex + 1, 0, newCoord);
      return next;
    });
    setWaypointLabels((prev) => {
      const next = [...prev];
      next.splice(afterIndex + 1, 0, '');
      return next;
    });
    setSelectedIndex(afterIndex + 1);
  }, []);

  // ─── Select a waypoint ────────────────────────────────────────────────────
  const handleSelectWaypoint = useCallback((index) => {
    setSelectedIndex((prev) => (prev === index ? null : index));
  }, []);

  // ─── Set a stop's label ───────────────────────────────────────────────────
  const handleSetWaypointLabel = useCallback((index, label) => {
    setWaypointLabels((prev) => {
      const next = [...prev];
      next[index] = label;
      return next;
    });
  }, []);

  // ─── Update an existing waypoint's coordinate (from stop search) ──────────
  const handleUpdateWaypointCoord = useCallback((index, coord, label) => {
    const newCoord = [parseFloat(coord[0].toFixed(6)), parseFloat(coord[1].toFixed(6))];
    setWaypoints((prev) => {
      const next = [...prev];
      next[index] = newCoord;
      return next;
    });
    if (label !== undefined) {
      setWaypointLabels((prev) => {
        const next = [...prev];
        next[index] = label;
        return next;
      });
    }
  }, []);

  // ─── Reorder waypoints (drag-and-drop in panel) ───────────────────────────
  const handleMoveWaypoint = useCallback((fromIndex, toIndex) => {
    if (toIndex < 0) return;
    setWaypoints((prev) => {
      if (toIndex >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
    setWaypointLabels((prev) => {
      if (toIndex >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
    setSelectedIndex(toIndex);
  }, []);

  /**
   * Add a stop at the end of the route (from geocoding in panel).
   * @param {[number, number]} coord
   * @param {string} label
   */
  const handleAddStop = useCallback((coord, label = '') => {
    const newCoord = [parseFloat(coord[0].toFixed(6)), parseFloat(coord[1].toFixed(6))];
    setWaypoints((prev) => [...prev, newCoord]);
    setWaypointLabels((prev) => [...prev, label]);
  }, []);

  // ─── Clear route ─────────────────────────────────────────────────────────
  const handleClearRoute = useCallback(() => {
    setWaypoints([]);
    setWaypointLabels([]);
    setRouteName('');
    setRouteDescription('');
    setSelectedIndex(null);
    setEditingRouteId(null);
    setDrawFromIndex(null);
    setGeneratedCoordinates(null);
    generatedAtRef.current = null;
  }, []);

  // ─── Save / update route ─────────────────────────────────────────────────
  const handleSaveRoute = useCallback(async () => {
    if (waypoints.length < 2) {
      toast.error('Add at least 2 waypoints to save a route.');
      return;
    }
    if (!routeName.trim()) {
      toast.error('Please enter a route name.');
      return;
    }
    setIsSaving(true);
    try {
      const payload = { name: routeName.trim(), description: routeDescription.trim(), geoJson };
      if (editingRouteId) {
        const res = await axios.patch(`${REACT_APP_API_ADMIN_ROUTES}/${editingRouteId}`, payload, {
          headers: getAuthHeaders(),
        });
        const updated = res?.data?.data || null;
        setSavedRoutes((prev) =>
          prev.map((r) => (r.id === editingRouteId ? { ...r, ...payload, ...(updated || {}) } : r)),
        );
        toast.success('Route updated successfully.');
        handleClearRoute();
        setRouteMapFocus(null);
      } else {
        const res = await axios.post(REACT_APP_API_ADMIN_ROUTES, payload, {
          headers: getAuthHeaders(),
        });
        const created = res?.data?.data || null;
        if (created) setSavedRoutes((prev) => [created, ...prev]);
        toast.success('Route saved successfully.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save route.');
    } finally {
      setIsSaving(false);
    }
  }, [waypoints, routeName, routeDescription, geoJson, editingRouteId, handleClearRoute]);

  // ─── Load all saved routes ────────────────────────────────────────────────
  /**
   * Normalise a route document so `id` is always a reliable 24-char hex string,
   * regardless of whether the toJSON plugin ran (id) or not (_id).
   */
  const normaliseRoute = (r) => {
    const id = r?.id || r?._id;
    return { ...r, id: id ? String(id) : undefined };
  };

  const handleLoadRoutes = useCallback(async () => {
    setIsLoadingRoutes(true);
    try {
      const res = await axios.get(REACT_APP_API_ADMIN_ROUTES, { headers: getAuthHeaders() });
      const raw =
        res?.data?.data?.docs || res?.data?.data?.routes || res?.data?.data || res?.data || [];
      setSavedRoutes((Array.isArray(raw) ? raw : []).map(normaliseRoute));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load routes.');
    } finally {
      setIsLoadingRoutes(false);
    }
  }, []);

  useEffect(() => {
    handleLoadRoutes();
  }, [handleLoadRoutes]);

  // ─── Load a single saved route into the editor ────────────────────────────
  const handleLoadRoute = useCallback((route) => {
    const coords = route?.geoJson?.geometry?.coordinates || [];
    setWaypoints(coords);
    setWaypointLabels(syncLabels([], 0, coords.length));
    setRouteName(route?.name || '');
    setRouteDescription(route?.description || '');
    setEditingRouteId(route?.id || null);
    setSelectedIndex(null);
    setGeneratedCoordinates(null);
    generatedAtRef.current = null;
    if (coords.length > 0) {
      setRouteMapFocus({ coords, at: Date.now() });
    }
    toast.info(`Loaded route: ${route?.name}`);
  }, []);

  // ─── Delete a saved route ─────────────────────────────────────────────────
  const MONGO_ID_RE = /^[0-9a-f]{24}$/i;

  const handleDeleteSavedRoute = useCallback(
    async (routeId) => {
      if (!routeId || !MONGO_ID_RE.test(String(routeId))) {
        toast.error('Cannot delete: route has an invalid ID. Try reloading the routes list.');
        return;
      }
      try {
        await axios.delete(`${REACT_APP_API_ADMIN_ROUTES}/${routeId}`, {
          headers: getAuthHeaders(),
        });
        setSavedRoutes((prev) => prev.filter((r) => r.id !== routeId));
        if (editingRouteId === routeId) handleClearRoute();
        toast.success('Route deleted.');
      } catch (err) {
        toast.error(err?.response?.data?.message || 'Failed to delete route.');
      }
    },
    [editingRouteId, handleClearRoute],
  );

  return {
    waypoints,
    waypointLabels,
    drawFromIndex,
    activeRouteColor,
    canvasRoutes,
    generatedCoordinates,
    isGenerating,
    isRouteStale,
    routeProfile,
    setRouteProfile,
    routeName,
    setRouteName,
    routeDescription,
    setRouteDescription,
    selectedIndex,
    savedRoutes,
    editingRouteId,
    isLoadingRoutes,
    isSaving,
    totalDistanceKm,
    geoJson,
    routeMapFocus,
    handleMapClick,
    handleWaypointDrag,
    handleDeleteWaypoint,
    handleInsertWaypoint,
    handleSelectWaypoint,
    handleSetWaypointLabel,
    handleUpdateWaypointCoord,
    handleMoveWaypoint,
    handleAddStop,
    handleSetDrawFrom,
    handleCancelDrawFrom,
    handleNewRoute,
    handleBranchFromStop,
    handleSwitchToRoute,
    handleRemoveCanvasRoute,
    handleClearRoute,
    handleGenerateRoute,
    handleClearGeneratedRoute,
    handleSaveRoute,
    handleLoadRoutes,
    handleLoadRoute,
    handleDeleteSavedRoute,
  };
};

export default useRouteDrawing;
